import { FC } from "react";

import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { IconButton, Link, TextInput, Tooltip } from "@portex-pro/ui-components";
import { ShipperQuoteRequest } from "api/rest/quote-requests";
import EmailPreviewSection from "app/pages/quote-request/_components/EmailPreviewSection";
import PortexDialog from "components/PortexDialog";
import Text from "components/Text";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";

interface ViewQuoteRequestDialogViewProps {
  quoteRequest: ShipperQuoteRequest;
  submissionLink: string;
  isOpen: boolean;
  onClose: () => void;
}

const ViewQuoteRequestDialogView: FC<ViewQuoteRequestDialogViewProps> = (props) => {
  const { quoteRequest, submissionLink, isOpen, onClose } = props;
  const { t } = useTranslation(["shipper"]);

  const { enqueueSnackbar } = useSnackbar();

  return (
    <PortexDialog
      title={t("shipper:viewQuoteRequest")}
      maxWidth="md"
      open={isOpen}
      onClose={onClose}
      hideCancelButton
      confirmButtonCopy={t("shipper:close")}
      onClickConfirm={onClose}
    >
      <div className="p-6 flex flex-col space-y-6 bg-grey-50">
        <div className="flex flex-col border border-grey-300 rounded-[4px] p-4 space-y-2 items-center bg-white">
          <Text size="x-large">{t("shipper:viewQuoteRequestDialog.submissionLink")}</Text>
          <Text size="x-small" weight="bold">
            {t("shipper:viewQuoteRequestDialog.description", { id: quoteRequest.portex_id })}
          </Text>

          <div className="flex w-full items-center justify-center">
            <TextInput
              disabled
              value={submissionLink}
              style={{ maxWidth: "100%", width: (submissionLink.length + 1) * 8 }}
            />
            <Tooltip title={t("shipper:viewQuoteRequestDialog.copy")} arrow placement="top">
              <IconButton
                color="primary"
                onClick={() => {
                  navigator.clipboard.writeText(submissionLink);
                  enqueueSnackbar(t("shipper:viewQuoteRequestDialog.copied"), {
                    variant: "success",
                    autoHideDuration: 2000,
                    anchorOrigin: {
                      vertical: "top",
                      horizontal: "center",
                    },
                    preventDuplicate: true,
                  });
                }}
              >
                <FileCopyOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("shipper:viewQuoteRequestDialog.openLink")} arrow placement="top">
              <Link href={submissionLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <IconButton>
                  <OpenInNewIcon />
                </IconButton>
              </Link>
            </Tooltip>
          </div>

          <Text size="x-small" weight="bold">
            <Trans i18nKey="viewQuoteRequestDialog.description2" ns="shipper">
              Anyone with the link can <u>view the details</u> below and <u>submit quotes</u> for this request
            </Trans>
          </Text>
        </div>

        <EmailPreviewSection quoteRequest={quoteRequest} />
      </div>
    </PortexDialog>
  );
};

export default ViewQuoteRequestDialogView;
